import { useState } from 'react';
import { PageLayout } from '../layouts/PageLayout';
import { SEOHead } from '../components/seo/SEOHead';
import { GlassPanel } from '../components/ui/GlassPanel';
import { SITE } from '../utils/seo';

const CONTACT_LINKS = [
  {
    label: 'Email',
    href: 'mailto:hello@symhos.dev',
    display: 'hello@symhos.dev',
    icon: '✉',
  },
  {
    label: 'GitHub',
    href: 'https://github.com/carsim',
    display: 'github.com/carsim',
    icon: '⌥',
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/in/simonecarani',
    display: 'linkedin.com/in/simonecarani',
    icon: '⊡',
  },
];

const CONTACT_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Simone Carani',
  url: `${SITE.url}/contact`,
  mainEntity: {
    '@type': 'Person',
    name: SITE.name,
    email: 'hello@symhos.dev',
    url: SITE.url,
  },
};

export function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | sent | error

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    // Placeholder: in production, wire to a form API (Formspree, Resend, etc.)
    setStatus('submitting');
    setTimeout(() => setStatus('sent'), 1200);
  };

  return (
    <PageLayout>
      <SEOHead
        title="Contact"
        description="Get in touch with Simone Carani."
        canonical={`${SITE.url}/contact`}
        jsonLd={[CONTACT_SCHEMA]}
      />

      <div className="max-w-2xl">
        <header className="mb-10">
          <h1 className="font-serif text-4xl text-[var(--color-text)] mb-2">Contact</h1>
          <p className="text-[var(--color-muted)]">
            Reach out for consulting, collaborations, or just to say hi.
          </p>
        </header>

        {/* Contact links */}
        <section aria-label="Contact information" className="mb-10">
          <ul className="flex flex-col gap-3">
            {CONTACT_LINKS.map(({ label, href, display, icon }) => (
              <li key={label}>
                <a
                  href={href}
                  target={href.startsWith('mailto') ? undefined : '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 group"
                  aria-label={`${label}: ${display}`}
                >
                  <span
                    aria-hidden="true"
                    className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/[0.04] border border-white/[0.07] text-[var(--color-accent)] text-base group-hover:bg-[var(--color-accent)]/10 transition-colors"
                  >
                    {icon}
                  </span>
                  <div>
                    <p className="text-xs text-[var(--color-muted)] uppercase tracking-widest">{label}</p>
                    <p className="text-sm text-[var(--color-text)] group-hover:text-[var(--color-accent)] transition-colors font-mono">
                      {display}
                    </p>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* Contact form */}
        <section aria-label="Contact form">
          <GlassPanel className="p-6">
            {status === 'sent' ? (
              <div role="status" aria-live="polite" className="text-center py-6">
                <p className="text-[var(--color-accent)] font-medium">Message sent — I'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                <h2 className="text-sm font-semibold text-[var(--color-muted)] uppercase tracking-widest mb-1">
                  Send a message
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-xs text-[var(--color-muted)] uppercase tracking-wide">Name</span>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      autoComplete="name"
                      className="input-field"
                      aria-required="true"
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-xs text-[var(--color-muted)] uppercase tracking-wide">Email</span>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      className="input-field"
                      aria-required="true"
                    />
                  </label>
                </div>

                <label className="flex flex-col gap-1.5">
                  <span className="text-xs text-[var(--color-muted)] uppercase tracking-wide">Message</span>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="input-field resize-none"
                    aria-required="true"
                  />
                </label>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="
                    self-start px-5 py-2 rounded-lg text-sm font-medium
                    bg-[var(--color-accent)]/15 text-[var(--color-accent)]
                    border border-[var(--color-accent)]/25
                    hover:bg-[var(--color-accent)]/25 transition-colors
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  {status === 'submitting' ? 'Sending…' : 'Send message'}
                </button>
              </form>
            )}
          </GlassPanel>
        </section>
      </div>
    </PageLayout>
  );
}
